import { useEffect, useState, useCallback, useMemo } from 'react'

enum ErrorType {
  MIN = 'min',
  MAX = 'max',
  STEPPER = 'stepper'
}

export const useInputKeypad = ({
  minAmount,
  maxAmount,
  stepper,
  defaultAmount,
  validationTrigger
}: {
  minAmount: number
  maxAmount: number
  stepper?: number
  defaultAmount?: number
  validationTrigger?: any
}) => {
  const [cursorPos, setCursorPos] = useState(0)
  const [enteredAmount, setEnteredAmount] = useState('')
  const [errorType, setErrorType] = useState<ErrorType | null>(null)
  const [enableValidation, setEnableValidation] = useState(false)

  const handleSetAmount = useCallback((value: string) => {
    setEnteredAmount(value)
  }, [])

  const changeAmount = useCallback((amount: number) => {
    if (amount > 0) {
      const amountString = amount.toString()
      setEnteredAmount(amountString)
      setCursorPos(amountString.length)
    } else if (amount === 0) {
      setEnteredAmount('')
      setCursorPos(0)
    }
  }, [])

  useEffect(() => {
    if (defaultAmount) {
      changeAmount(defaultAmount)
    }
  }, [defaultAmount, changeAmount])

  const validateAmount = useCallback(
    (value: string, enableValidationParam: boolean): ErrorType | null => {
      // Default Handlings
      if (!value) {
        return null
      }

      const amount = parseFloat(value)
      if (isNaN(amount)) {
        return null
      }

      // If validation is not enabled, only check for MAX error
      if (!enableValidationParam) {
        if (amount > maxAmount) {
          return ErrorType.MAX
        }
        return null
      }

      // Full validation when enableValidation is true
      // Check bounds
      if (amount < minAmount) {
        // 1. check for MIN error
        return ErrorType.MIN
      }

      if (amount > maxAmount) {
        // 2. check for MAX error
        return ErrorType.MAX
      }

      // 3. Check stepper validation
      const EPSILON = 1e-10

      if (typeof stepper === 'number' && stepper > 0) {
        const steps = amount / stepper

        if (Math.abs(steps - Math.round(steps)) > EPSILON) {
          return ErrorType.STEPPER
        }
      }

      return null
    },
    [minAmount, maxAmount, stepper]
  )

  const handleNumberClick = useCallback(
    (num: number) => {
      if (errorType === ErrorType.MAX) return

      // Prevent leading zeros
      if (num === 0 && enteredAmount === '') {
        return
      }

      // Check if adding this number would create a leading zero
      if (num === 0 && cursorPos === 0 && enteredAmount?.length > 0) {
        return
      }

      // Use functional updates for better multitap support
      setEnteredAmount((prev) => {
        const newAmount = prev.slice(0, cursorPos) + num + prev.slice(cursorPos)
        return newAmount
      })

      setCursorPos((prev) => prev + 1)
    },
    [cursorPos, errorType]
  )

  const handleBackspaceClick = useCallback(() => {
    // Use functional updates for better multitap support
    setCursorPos((prev) => {
      if (prev === 0) return prev
      return prev - 1
    })

    setEnteredAmount((prev) => {
      if (cursorPos === 0) return prev
      return prev.slice(0, cursorPos - 1) + prev.slice(cursorPos)
    })
  }, [cursorPos])

  const handleCursorChange = useCallback((pos: number) => {
    setCursorPos(pos)
  }, [])

  // Debounced validation to reduce unnecessary checks - only when validation is enabled
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const error = validateAmount(enteredAmount, enableValidation)
      setErrorType(error)

      // If MAX error is encountered and validation is not enabled, enable it
      if (error === ErrorType.MAX && !enableValidation) {
        setEnableValidation(true)
      }
    }, 100) // 100ms debounce

    return () => clearTimeout(timeoutId)
  }, [enteredAmount, validateAmount, validationTrigger, enableValidation])

  // Function to manually trigger validation and return validation result
  const triggerValidation = useCallback(() => {
    if (!enteredAmount) return null
    setEnableValidation(true)
    return validateAmount(enteredAmount, true)
  }, [enteredAmount, validateAmount])

  return {
    handleNumberClick,
    handleBackspaceClick,
    handleCursorChange,
    errorType,
    cursorPos,
    enteredAmount,
    handleSetAmount,
    changeAmount,
    triggerValidation
  }
}
